/* ==========================================================================
   Freshfield — Magnesium Reality Check landing page
   File: assets/lp-mg.js            Progressive enhancement only.

   One job: drive the four-step guided label reveal. No calculator, no
   storage, no network calls, no third-party code.

   All human-readable strings arrive from the section's
   <script type="application/json" data-ffm-strings> element, which is
   populated from the theme locale files (| t + | json). That keeps every
   visible word — including text this script writes at runtime — in the
   translation source of truth.

   Markup contract:
     [data-ffm-reveal] ............ one per reveal section
       [data-ffm-strings] ......... JSON of localized strings
       [data-ffm-progress] ........ aria-live progress line
       [data-ffm-restart] ......... reset to step 1
       [data-ffm-stage="1..4"] .... <article>, hidden unless active
         [data-ffm-go="n"] ........ advance to step n
     [data-ffm-start="#id"] ....... any button that opens a reveal
   ========================================================================== */
(function () {
  'use strict';

  /* Fallback stage count; the live value arrives with the localized strings. */
  var FALLBACK_STAGE_COUNT = 4;

  var reduceMotion = false;

  try {
    reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (err) {
    reduceMotion = false;
  }

  function fill(pattern, index, total) {
    return String(pattern)
      .replace(/\{\{\s*index\s*\}\}/g, String(index))
      .replace(/\{\{\s*total\s*\}\}/g, String(total));
  }

  function readStrings(root) {
    var node = root.querySelector('[data-ffm-strings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}') || {};
    } catch (err) {
      return {};
    }
  }

  function stageList(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-ffm-stage]'));
  }

  function Controller(root) {
    this.root = root;
    this.strings = readStrings(root);
    this.stages = stageList(root);
    this.progress = root.querySelector('[data-ffm-progress]');
    this.active = 0;
  }

  Controller.prototype.focusStage = function (stage) {
    var target = stage.querySelector('[data-ffm-focus]') || stage.querySelector('h2, h3, h4');
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    try {
      target.focus({ preventScroll: true });
    } catch (err) {
      target.focus();
    }
  };

  Controller.prototype.show = function (index) {
    if (!this.stages.length) return;
    var bounded = Math.min(Math.max(index, 1), this.stages.length);

    this.root.hidden = false;
    this.stages.forEach(function (stage, i) {
      stage.hidden = i + 1 !== bounded;
    });

    if (this.progress) {
      var pattern = this.strings.progressPattern;
      this.progress.textContent = pattern ? fill(pattern, bounded, this.total()) : '';
      if (!this.progress.hasAttribute('aria-live')) {
        this.progress.setAttribute('aria-live', 'polite');
      }
    }

    var current = this.stages[bounded - 1];
    try {
      this.root.scrollIntoView({
        block: 'start',
        behavior: reduceMotion ? 'instant' : 'smooth'
      });
    } catch (err) {
      this.root.scrollIntoView();
    }
    if (current) this.focusStage(current);
    this.active = bounded;
  };

  Controller.prototype.total = function () {
    var declared = parseInt(this.strings.total, 10);
    if (declared > 0) return declared;
    return this.stages.length || FALLBACK_STAGE_COUNT;
  };

  Controller.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('click', function (event) {
      var node = event.target.closest ? event.target.closest('[data-ffm-go], [data-ffm-restart]') : null;
      if (!node || !self.root.contains(node)) return;
      event.preventDefault();
      if (node.hasAttribute('data-ffm-restart')) {
        self.show(1);
        return;
      }
      self.show(parseInt(node.getAttribute('data-ffm-go'), 10) || 1);
    });
  };

  function boot() {
    var roots = document.querySelectorAll('[data-ffm-reveal]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute('data-ffm-ready') === '1') return;
      root.setAttribute('data-ffm-ready', '1');
      new Controller(root).bind();
    });

    var openers = document.querySelectorAll('[data-ffm-start]');
    Array.prototype.forEach.call(openers, function (button) {
      button.addEventListener('click', function (event) {
        var selector = button.getAttribute('data-ffm-start');
        var target = selector ? document.querySelector(selector) : null;
        if (!target) return;
        event.preventDefault();
        var stages = stageList(target);
        var strings = readStrings(target);
        var declared = parseInt(strings.total, 10);
        var total = declared > 0 ? declared : (stages.length || FALLBACK_STAGE_COUNT);
        target.hidden = false;
        stages.forEach(function (stage, i) { stage.hidden = i !== 0; });
        var progress = target.querySelector('[data-ffm-progress]');
        if (progress && strings.progressPattern) {
          progress.textContent = fill(strings.progressPattern, 1, total);
        }
        try {
          target.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'instant' : 'smooth' });
        } catch (err) {
          target.scrollIntoView();
        }
        var first = stages[0];
        if (first) {
          var heading = first.querySelector('[data-ffm-focus]') || first.querySelector('h2, h3, h4');
          if (heading) {
            if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
            try { heading.focus({ preventScroll: true }); } catch (err) { heading.focus(); }
          }
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
